import { Link, useLocation } from 'react-router-dom';

import { NavSection, cn } from '@tankef/ui';
import type { NavItemData } from '@tankef/ui';

import { useNavData } from '../config-nav';

import { useUnreadCount } from '@/domains/notifications/api';

interface NavVerticalProps {
  collapsed: boolean;
}

export function NavVertical({ collapsed }: NavVerticalProps) {
  const location = useLocation();
  const navData = useNavData();
  const unreadCount = useUnreadCount();

  const renderNavLink = ({
    item,
    children,
    className,
    onClick,
  }: {
    item: NavItemData;
    children: React.ReactNode;
    className: string;
    onClick?: () => void;
  }) => {
    // Show notification red dot on notifications nav item
    const hasNotificationDot = item.path?.includes('notifications') && unreadCount > 0;

    // Only expose data-tour on desktop — NavVertical is display:none on mobile/tablet
    // so querySelector would find it first and return {0,0,0,0} coords,
    // breaking the tour highlight when the mobile drawer is open.
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    const tourAttr = isDesktop
      ? item.key === 'wallet'
        ? 'nav-wallet'
        : item.key === 'investments'
          ? 'nav-investments'
          : item.key === 'savings-box'
            ? 'nav-savings'
            : undefined
      : undefined;

    return (
      <Link to={item.path || '#'} className={className} onClick={onClick} data-tour={tourAttr}>
        <div className="relative flex items-center w-full">
          {children}
          {hasNotificationDot && (
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-error-600 rounded-full" />
          )}
        </div>
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        'hidden flex-1 shrink-0 flex-col border-r border-tkf-line bg-tkf-surface lg:flex',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-20' : 'w-[280px]'
      )}
    >
      {/* Navigation */}
      <div className="flex-1 overflow-y-auto p-3">
        <NavSection
          data={navData}
          collapsed={collapsed}
          activeKey={location.pathname}
          renderLink={renderNavLink}
        />
      </div>
    </aside>
  );
}
